import { HttpStatusCode } from '@angular/common/http';

/**
 * Builds a user-facing message from an API error (ProblemDetails). Order: `detail`, validation `errors`
 * (array of `{ detail: "Field: message" }`, legacy `{ Field: [messages] }` dictionary also tolerated),
 * `title`, `message`, then the fallback.
 */
export function resolveApiErrorMessage(fallback: string, error: unknown): string {
  if (typeof error !== 'object' || error === null) {
    return fallback;
  }

  const errorRecord = error as { error?: unknown; message?: unknown };
  const payload = errorRecord.error;

  if (typeof payload === 'string' && payload.trim()) {
    return payload.trim();
  }

  if (typeof payload === 'object' && payload !== null) {
    const record = payload as Record<string, unknown>;

    const detail = asText(record['detail']);
    if (detail) {
      return detail;
    }

    const validationMessages = readValidationMessages(record['errors']);
    if (validationMessages.length > 0) {
      return validationMessages.join(' ');
    }

    const title = asText(record['title']) || asText(record['message']);
    if (title) {
      return title;
    }
  }

  return asText(errorRecord.message) || fallback;
}

export function isConflictError(error: unknown): boolean {
  return getErrorStatus(error) === HttpStatusCode.Conflict;
}

export function getErrorStatus(error: unknown): number | null {
  if (typeof error !== 'object' || error === null) {
    return null;
  }

  const status = (error as { status?: unknown }).status;
  return typeof status === 'number' ? status : null;
}

function readValidationMessages(errors: unknown): string[] {
  if (Array.isArray(errors)) {
    return errors
      .map((item) => (typeof item === 'string' ? item.trim() : asText((item as { detail?: unknown } | null)?.detail)))
      .filter((message): message is string => !!message);
  }

  if (typeof errors === 'object' && errors !== null) {
    return Object.entries(errors as Record<string, unknown>).flatMap(([field, messages]) =>
      (Array.isArray(messages) ? messages : [messages])
        .map((message) => asText(message))
        .filter((message): message is string => !!message)
        .map((message) => (field ? `${field}: ${message}` : message)),
    );
  }

  return [];
}

function asText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}
